import React, { useState, useEffect } from 'react';
import { BarChart, Bar, LabelList, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Building2, Clock, TrendingUp, Ticket, CheckCircle, CreditCard, DollarSign, ArrowLeft, Calendar } from 'lucide-react';

const ProvincialAdminDashboard = () => {
  const [currentView, setCurrentView] = useState('dashboard');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);

  // Realistic sample ticket data with dates and destination assignment
  const rawTicketData = [
    // December 2025 data (around current date: Dec 22, 2025)
    ...Array(150).fill().map((_, i) => {
      const day = Math.floor(Math.random() * 22) + 1; // Dec 1-22
      return {
        date: `2025-12-${day.toString().padStart(2, '0')}`,
        localAdult: Math.floor(Math.random() * 120) + 30,
        localChild: Math.floor(Math.random() * 50) + 10,
        foreignAdult: Math.floor(Math.random() * 100) + 20,
        foreignChild: Math.floor(Math.random() * 40) + 5,
        destinationId: Math.floor(Math.random() * 6) + 1,
      };
    }),
    // Add some older data for variety
    ...Array(50).fill().map((_, i) => {
      const month = Math.floor(Math.random() * 11) + 1;
      const day = Math.floor(Math.random() * 28) + 1;
      return {
        date: `2025-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`,
        localAdult: Math.floor(Math.random() * 80) + 20,
        localChild: Math.floor(Math.random() * 30) + 5,
        foreignAdult: Math.floor(Math.random() * 70) + 15,
        foreignChild: Math.floor(Math.random() * 25) + 3,
        destinationId: Math.floor(Math.random() * 6) + 1,
      };
    })
  ];

  const [registeredDestinations] = useState([
    { id: 1, name: 'Sigiriya Rock Fortress', registeredDate: '2023-01-15', ticketPrice: 6000, commissionRate: 0.06 },
    { id: 2, name: 'Yala National Park', registeredDate: '2023-02-20', ticketPrice: 5000, commissionRate: 0.06 },
    { id: 3, name: 'Temple of the Tooth', registeredDate: '2023-03-10', ticketPrice: 4000, commissionRate: 0.06 },
    { id: 4, name: 'Galle Fort', registeredDate: '2023-04-05', ticketPrice: 3000, commissionRate: 0.06 },
    { id: 5, name: 'Pinnawala Elephant Orphanage', registeredDate: '2023-05-12', ticketPrice: 4500, commissionRate: 0.06 },
    { id: 6, name: 'Dambulla Cave Temple', registeredDate: '2023-06-08', ticketPrice: 3500, commissionRate: 0.06 }
  ]);

  const [pendingDestinations] = useState([
    { id: 1, name: 'Horton Plains National Park', submittedDate: '2025-12-15', applicantName: 'Wildlife Department', status: 'Under Review' },
    { id: 2, name: 'Minneriya National Park', submittedDate: '2025-12-10', applicantName: 'Tourism Board', status: 'Document Verification' },
    { id: 3, name: 'Arugam Bay Beach', submittedDate: '2025-12-08', applicantName: 'Beach Resort Ltd', status: 'Under Review' },
    { id: 4, name: 'Knuckles Mountain Range', submittedDate: '2025-12-05', applicantName: 'Adventure Tours', status: 'Pending Approval' },
    { id: 5, name: 'Ella Rock Viewpoint', submittedDate: '2025-12-01', applicantName: 'Hill Country Tourism', status: 'Under Review' }
  ]);

  const [newOnboardings] = useState([
    { id: 1, name: 'Udawalawe National Park', onboardedDate: '2025-12-20', category: 'Wildlife', initialTickets: 145 },
    { id: 2, name: 'Polonnaruwa Ancient City', onboardedDate: '2025-12-18', category: 'Historical', initialTickets: 289 },
    { id: 3, name: 'Mirissa Whale Watching', onboardedDate: '2025-12-15', category: 'Marine', initialTickets: 356 },
    { id: 4, name: 'Sinharaja Rain Forest', onboardedDate: '2025-12-10', category: 'Nature', initialTickets: 167 },
    { id: 5, name: 'Bentota Beach Resort', onboardedDate: '2025-12-05', category: 'Beach', initialTickets: 534 }
  ]);

  const yearlyData = [
    { year: '2020', income: 1200000 },
    { year: '2021', income: 1850000 },
    { year: '2022', income: 2100000 },
    { year: '2023', income: 2650000 },
    { year: '2024', income: 2845600 },
    { year: '2025', income: 4528900 }
  ];

  const destinationData = [
    { name: 'Sigiriya Rock', value: 8200, percentage: 32.1 },
    { name: 'Yala National Park', value: 6100, percentage: 23.9 },
    { name: 'Temple of Tooth', value: 4890, percentage: 19.1 },
    { name: 'Others', value: 6350, percentage: 24.9 }
  ];

  const COLORS = ['#0055A5', '#d97706', '#00918f', '#94a3b8'];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 1000000) return `LKR ${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `LKR ${(amount / 1000).toFixed(0)}K`;
    return `LKR ${amount}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Filter tickets based on date range
  const filteredTickets = rawTicketData.filter(ticket => {
    if (!dateRange.start || !dateRange.end) return true;
    return ticket.date >= dateRange.start && ticket.date <= dateRange.end;
  });

  const breakdown = {
    localAdult: filteredTickets.reduce((sum, t) => sum + t.localAdult, 0),
    localChild: filteredTickets.reduce((sum, t) => sum + t.localChild, 0),
    foreignAdult: filteredTickets.reduce((sum, t) => sum + t.foreignAdult, 0),
    foreignChild: filteredTickets.reduce((sum, t) => sum + t.foreignChild, 0)
  };

  const totalTickets = breakdown.localAdult + breakdown.localChild + breakdown.foreignAdult + breakdown.foreignChild;
  const voidTickets = Math.floor(totalTickets * 0.93); // Realistic check-in rate

  // Revenue calculation per category (using average destination price or per-destination)
  const revenueBreakdown = {
    localAdult: breakdown.localAdult * 4000, // assuming average price, adjust if needed
    localChild: breakdown.localChild * 2000,
    foreignAdult: breakdown.foreignAdult * 6000,
    foreignChild: breakdown.foreignChild * 3000
  };

  const totalRevenue = Object.values(revenueBreakdown).reduce((sum, val) => sum + val, 0);
  const totalCommission = totalRevenue * 0.06;

  // Destination-wise data
  const destinationCommissionData = registeredDestinations.map(dest => {
    const destTickets = filteredTickets.filter(t => t.destinationId === dest.id);
    const ticketsSold = destTickets.reduce((sum, t) => sum + t.localAdult + t.localChild + t.foreignAdult + t.foreignChild, 0);
    const revenue = ticketsSold * dest.ticketPrice;
    const commission = revenue * dest.commissionRate;
    return { ...dest, ticketsSold, revenue, commission };
  });

  const StatCard = ({ icon: Icon, title, value, subtitle, iconBg, iconColor, plain, onClick }) => (
    <div
      className="p-6 transition-shadow bg-white border cursor-pointer rounded-xl border-slate-200 hover:shadow-sm"
      onClick={onClick}
    >
      <div className="flex items-start justify-between mb-4">
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <div className={`p-2.5 rounded-lg ${iconBg}`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
      <p className="mb-2 text-4xl font-bold text-slate-900">{value}</p>
      {subtitle && (
        plain
          ? <p className="text-sm text-slate-500">{subtitle}</p>
          : <p className="text-sm font-semibold text-brand-blue">{subtitle} &rarr;</p>
      )}
    </div>
  );

  const RegisteredDestinationsView = () => (
    <div className="space-y-4">
      <button
        onClick={() => setCurrentView('dashboard')}
        className="flex items-center font-medium text-brand-blue hover:text-brand-blue-dark"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold text-slate-900">Registered Destinations</h2>

      <div className="flex gap-3">
        <input
          type="date"
          value={dateRange.start}
          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
          className="px-3 py-2 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
        />
        <input
          type="date"
          value={dateRange.end}
          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
          className="px-3 py-2 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
        />
        <button
          onClick={() => setDateRange({ start: '', end: '' })}
          className="px-4 py-2 text-sm font-semibold border rounded-lg text-slate-600 border-slate-300 hover:bg-slate-50"
        >
          Clear
        </button>
      </div>

      <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-slate-50 border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Destination Name</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Registered Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Tickets Sold</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {destinationCommissionData.map((dest) => (
                <tr key={dest.id} className="hover:bg-slate-50">
                  <td className="px-6 py-3 text-sm font-medium whitespace-nowrap text-slate-800">{dest.name}</td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap text-slate-600">{formatDate(dest.registeredDate)}</td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap text-slate-800">{dest.ticketsSold.toLocaleString()}</td>
                  <td className="px-6 py-3 text-sm font-semibold whitespace-nowrap text-emerald-600">{formatCurrency(dest.revenue)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t-2 bg-slate-50 border-slate-200">
              <tr>
                <td className="px-6 py-4 text-sm font-bold text-slate-900" colSpan="2">Total</td>
                <td className="px-6 py-4 text-sm font-bold text-slate-900">
                  {destinationCommissionData.reduce((sum, d) => sum + d.ticketsSold, 0).toLocaleString()}
                </td>
                <td className="px-6 py-4 text-sm font-bold text-emerald-600">
                  {formatCurrency(destinationCommissionData.reduce((sum, d) => sum + d.revenue, 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );

  const PendingDestinationsView = () => (
    <div className="space-y-4">
      <button
        onClick={() => setCurrentView('dashboard')}
        className="flex items-center font-medium text-brand-blue hover:text-brand-blue-dark"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold text-slate-900">Pending Destination Registrations</h2>
      <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-slate-50 border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Destination Name</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Applicant</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Submitted Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pendingDestinations.map((dest) => (
                <tr key={dest.id} className="hover:bg-slate-50">
                  <td className="px-6 py-3 text-sm font-medium whitespace-nowrap text-slate-800">{dest.name}</td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap text-slate-600">{dest.applicantName}</td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap text-slate-600">{formatDate(dest.submittedDate)}</td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-amber-50 text-amber-700">
                      {dest.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap">
                    <button className="mr-3 font-medium text-brand-blue hover:text-brand-blue-dark">Review</button>
                    <button className="font-medium text-emerald-600 hover:text-emerald-700">Approve</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const NewOnboardingsView = () => (
    <div className="space-y-4">
      <button
        onClick={() => setCurrentView('dashboard')}
        className="flex items-center font-medium text-brand-blue hover:text-brand-blue-dark"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to Dashboard
      </button>
      <h2 className="text-2xl font-bold text-slate-900">New Onboarding Destinations (This Month)</h2>
      <div className="overflow-hidden bg-white border rounded-xl border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-slate-50 border-slate-200">
              <tr>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Destination Name</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Category</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Onboarded Date</th>
                <th className="px-6 py-3 text-xs font-semibold text-left text-slate-500">Initial Tickets Sold</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {newOnboardings.map((dest) => (
                <tr key={dest.id} className="hover:bg-slate-50">
                  <td className="px-6 py-3 text-sm font-medium whitespace-nowrap text-slate-800">{dest.name}</td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-brand-blue/10 text-brand-blue">
                      {dest.category}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap text-slate-600">{formatDate(dest.onboardedDate)}</td>
                  <td className="px-6 py-3 text-sm whitespace-nowrap text-slate-800">{dest.initialTickets.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const DashboardView = () => {
    useEffect(() => {
      if (!dateRange.start) {
        const today = '2025-12-22';
        setDateRange({ start: today, end: today });
      }
    }, []);

    return (
      <>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Provincial Council Dashboard</h1>
            <p className="mt-1 text-sm text-slate-500">Tourism destination management &amp; analytics</p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Calendar className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="py-2 pl-9 pr-3 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
              />
            </div>
            <span className="text-sm text-slate-400">to</span>
            <div className="relative">
              <Calendar className="absolute w-4 h-4 -translate-y-1/2 pointer-events-none left-3 top-1/2 text-slate-400" />
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="py-2 pl-9 pr-3 text-sm border rounded-lg border-slate-300 focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
              />
            </div>
            <button
              onClick={() => {
                const today = '2025-12-22';
                setDateRange({ start: today, end: today });
              }}
              className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-brand-blue hover:bg-brand-blue-dark"
            >
              Today
            </button>
          </div>
        </div>

        {/* Destination Stats */}
        <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
          <StatCard
            icon={Building2}
            title="Registered Destinations"
            value={registeredDestinations.length}
            subtitle="View list"
            iconBg="bg-brand-blue/10"
            iconColor="text-brand-blue"
            onClick={() => setCurrentView('registered')}
          />
          <StatCard
            icon={Clock}
            title="Pending Registrations"
            value={pendingDestinations.length}
            subtitle="Review"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            onClick={() => setCurrentView('pending')}
          />
          <StatCard
            icon={TrendingUp}
            title="New Onboardings (Monthly)"
            value={newOnboardings.length}
            subtitle="View details"
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            onClick={() => setCurrentView('newOnboardings')}
          />
        </div>

        {/* Tickets & Revenue Row */}
        <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-3">
          <StatCard
            icon={Ticket}
            title="Total Tickets Sold"
            value={totalTickets.toLocaleString()}
            subtitle="Ticket breakdown"
            iconBg="bg-brand-blue/10"
            iconColor="text-brand-blue"
            onClick={() => setShowTicketModal(true)}
          />
          <StatCard
            icon={CheckCircle}
            title="Checked-In Tickets"
            value={voidTickets.toLocaleString()}
            subtitle={`${totalTickets ? ((voidTickets / totalTickets) * 100).toFixed(1) : 0}% check-in rate`}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            plain
            onClick={() => setShowTicketModal(true)}
          />
          <StatCard
            icon={CreditCard}
            title="Total Ticket Revenue"
            value={formatCurrency(totalRevenue)}
            subtitle="Revenue breakdown"
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            onClick={() => setShowRevenueModal(true)}
          />
        </div>

        {/* Commission Card */}
        <div
          className="flex items-center justify-between p-6 mb-6 transition-shadow bg-white border-l-4 cursor-pointer rounded-xl border-y border-r border-slate-200 border-l-brand-blue hover:shadow-sm"
          onClick={() => setShowCommissionModal(true)}
        >
          <div>
            <p className="mb-1 text-sm font-medium text-slate-500">Total Commission Earned</p>
            <p className="text-4xl font-bold text-slate-900">{formatCurrency(totalCommission)}</p>
            <p className="mt-2 text-sm text-slate-500">
              6% of total ticket revenue across all registered destinations &middot; <span className="font-semibold text-brand-blue">View breakdown &rarr;</span>
            </p>
          </div>
          <div className="p-4 rounded-xl bg-brand-blue/10">
            <DollarSign size={32} className="text-brand-blue" />
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-2">
          <div className="p-6 bg-white border rounded-xl border-slate-200">
            <h2 className="mb-5 text-base font-bold text-slate-800">Year-wise Income Comparison</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={yearlyData} margin={{ top: 24, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: '#64748b' }} />
                <YAxis hide />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Bar dataKey="income" fill="#0055A5" name="Income (LKR)" radius={[6, 6, 0, 0]} maxBarSize={56}>
                  <LabelList
                    dataKey="income"
                    position="top"
                    formatter={formatCompactCurrency}
                    style={{ fontSize: 12, fontWeight: 600, fill: '#334155' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="p-6 bg-white border rounded-xl border-slate-200">
            <h2 className="text-base font-bold text-slate-800">Top Ticket-Selling Destinations</h2>
            <p className="mb-5 text-sm text-slate-500">Share of total tickets sold</p>
            <div className="space-y-5">
              {destinationData.map((dest, index) => (
                <div key={index}>
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="text-base font-semibold" style={{ color: COLORS[index] }}>{dest.name}</span>
                    <span className="text-sm text-slate-500">{dest.value.toLocaleString()} &middot; {dest.percentage}%</span>
                  </div>
                  <div className="w-full h-2.5 rounded-full bg-slate-100">
                    <div
                      className="h-2.5 rounded-full"
                      style={{ width: `${dest.percentage}%`, backgroundColor: COLORS[index] }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Ticket Breakdown Modal - Reduced size */}
        {showTicketModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 mt-20">
            <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-xl font-bold mb-5 text-gray-900">Ticket Sales Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 font-medium">Local Adult</span>
                  <span className="font-bold text-brand-blue text-lg">{breakdown.localAdult.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 font-medium">Local Child</span>
                  <span className="font-bold text-brand-blue text-lg">{breakdown.localChild.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 font-medium">Foreign Adult</span>
                  <span className="font-bold text-teal-600 text-lg">{breakdown.foreignAdult.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600 font-medium">Foreign Child</span>
                  <span className="font-bold text-teal-500 text-lg">{breakdown.foreignChild.toLocaleString()}</span>
                </div>
                <div className="border-t-2 border-slate-100 pt-4 mt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-slate-800">Total Tickets</span>
                    <span className="text-slate-900">{totalTickets.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowTicketModal(false)}
                className="mt-6 w-full px-5 py-2.5 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-dark font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Revenue Breakdown Modal - Reduced size */}
        {showRevenueModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 mt-20">
            <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl">
              <h3 className="text-xl font-bold mb-5 text-slate-900">Revenue Breakdown</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 font-medium">Local Adult</span>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{breakdown.localAdult.toLocaleString()} tickets</p>
                    <p className="font-bold text-brand-blue text-lg">{formatCurrency(revenueBreakdown.localAdult)}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 font-medium">Local Child</span>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{breakdown.localChild.toLocaleString()} tickets</p>
                    <p className="font-bold text-brand-blue text-lg">{formatCurrency(revenueBreakdown.localChild)}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-slate-100">
                  <span className="text-slate-600 font-medium">Foreign Adult</span>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{breakdown.foreignAdult.toLocaleString()} tickets</p>
                    <p className="font-bold text-teal-600 text-lg">{formatCurrency(revenueBreakdown.foreignAdult)}</p>
                  </div>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-slate-600 font-medium">Foreign Child</span>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">{breakdown.foreignChild.toLocaleString()} tickets</p>
                    <p className="font-bold text-teal-500 text-lg">{formatCurrency(revenueBreakdown.foreignChild)}</p>
                  </div>
                </div>
                <div className="border-t-2 border-slate-100 pt-4 mt-4">
                  <div className="flex justify-between text-xl font-bold">
                    <span className="text-slate-800">Total Revenue</span>
                    <span className="text-slate-900">{formatCurrency(totalRevenue)}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowRevenueModal(false)}
                className="mt-6 w-full px-5 py-2.5 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-dark font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}

        {/* Commission Breakdown Modal - Slightly reduced but still wide enough for table */}
        {showCommissionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 mt-20">
            <div className="bg-white rounded-xl p-6 max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl">
              <h3 className="text-xl font-bold mb-5 text-slate-900">Commission Breakdown by Destination</h3>

              <div className="mb-5 flex gap-3">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
                />
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Destination</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Tickets Sold</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Commission (6%)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {destinationCommissionData.map((dest) => (
                      <tr key={dest.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-800">{dest.name}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{dest.ticketsSold.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-slate-700">{formatCurrency(dest.revenue)}</td>
                        <td className="px-4 py-3 text-sm font-semibold text-emerald-600">{formatCurrency(dest.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-100 font-bold">
                    <tr>
                      <td className="px-4 py-4 text-slate-800">Total</td>
                      <td className="px-4 py-4 text-slate-800">{destinationCommissionData.reduce((s, d) => s + d.ticketsSold, 0).toLocaleString()}</td>
                      <td className="px-4 py-4 text-slate-800">{formatCurrency(totalRevenue)}</td>
                      <td className="px-4 py-4 text-emerald-600">{formatCurrency(totalCommission)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <button
                onClick={() => setShowCommissionModal(false)}
                className="mt-6 w-full px-5 py-2.5 bg-brand-blue text-white rounded-lg hover:bg-brand-blue-dark font-medium"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div>
      {currentView === 'dashboard' && <DashboardView />}
      {currentView === 'registered' && <RegisteredDestinationsView />}
      {currentView === 'pending' && <PendingDestinationsView />}
      {currentView === 'newOnboardings' && <NewOnboardingsView />}
    </div>
  );
};

export default ProvincialAdminDashboard;